import { Search, Bell, ChevronDown, Video } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/routes";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useWaitingMembersCount } from "@/hooks/useWaitingMembersCount";

export function ProviderHeader() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const waitingCount = useWaitingMembersCount();
  const displayName = user?.user_metadata?.display_name || user?.email?.split("@")[0] || "Provider";
  const initials = displayName.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2);

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-background/95 px-6 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      {/* Search */}
      <div className="relative w-96">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Search patients, claims, devices..."
          className="pl-10 bg-muted/50 border-0 focus-visible:ring-1"
        />
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-4">
        {/* Telehealth Quick Access */}
        <Button
          variant={waitingCount > 0 ? "default" : "outline"}
          size="sm"
          onClick={() => navigate(ROUTES.PROVIDER_TELEHEALTH)}
          className="relative gap-2"
        >
          <Video className="h-4 w-4" />
          <span className="hidden sm:inline">Virtual Sessions</span>
          {waitingCount > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive text-[10px] font-semibold text-destructive-foreground animate-pulse px-1">
              {waitingCount}
            </span>
          )}
        </Button>

        {/* Notifications */}
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-medium text-destructive-foreground">
            5
          </span>
        </Button>

        {/* User Menu */}
        <div className="flex items-center gap-3 rounded-lg px-2 py-1.5 transition-colors hover:bg-muted/50 cursor-pointer">
          <Avatar className="h-8 w-8">
            <AvatarImage src="" />
            <AvatarFallback className="bg-primary text-primary-foreground text-sm">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="hidden md:block">
            <p className="text-sm font-medium text-foreground">{displayName}</p>
            <Badge variant="secondary" className="text-xs">Provider</Badge>
          </div>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
    </header>
  );
}
